import React, { useMemo } from "react";
import type { FormikErrors } from "formik";
import { type FormikFormChildProps } from "../FormikForm/FormikFormWrapperForJetpackConfig";
import type { Config } from "kadow-jetpack-model";
import { LANGUAGE } from "../../../../utils/i18n/i18n";
import KC_Padder from "../../../../designsystem/KC_Padder/KC_Padder";
import NiceModal from "@ebay/nice-modal-react";
import GiftSuggestionModal from "../../../../components/modal/Onboarding/GiftSuggestion/GiftSuggestionModal";
import type {
  LoyaltyConfig,
  LoyaltyTier,
  LoyaltyTierPrize
} from "kadow-jetpack-model";
import { buildDefaultTierPrize } from "../Loyalty/helper/defaultLoyaltyTiers";
import { getDefaultTierTokenReward } from "kadow-jetpack-model";
import { useAccountToDisplaySelector } from "../../../../redux/actions/user";
import { LoyaltyRewardEditorPageLayout } from "./component/LoyaltyRewardLayouts";
import TierEditorCard from "./TierEditorCard";
import "../Loyalty/styles.scss";
import "./styles.scss";
import { useSubscriptionPlansSelector } from "../../../../redux/actions/app";

interface LoyaltyTiersGiftsEditorProps extends FormikFormChildProps {
  errors: FormikErrors<Config>;
}

const LoyaltyTiersGiftsEditor = (props: LoyaltyTiersGiftsEditorProps) => {
  const { config, setFieldValue } = props;
  const { accountToDisplay } = useAccountToDisplaySelector();
  const subscriptionPlans = useSubscriptionPlansSelector();
  const loyaltyConfig = (config.lty || {
    enabled: false,
    tiers: []
  }) as LoyaltyConfig;
  const tiers: LoyaltyTier[] = (loyaltyConfig.tiers || []) as LoyaltyTier[];

  const hasAdvancedCouponOptions = useMemo(
    () => accountToDisplay?.saasFeatures?.adc || false,
    [accountToDisplay?.saasFeatures?.adc]
  );

  const currentLang = useMemo(() => (LANGUAGE === "fr" ? "fr" : "en"), []);

  const handleTierRewardType = (tierIndex: number, isPrize: boolean) => {
    const tier = tiers[tierIndex];
    const newTiers = [...tiers];
    if (isPrize) {
      newTiers[tierIndex] = {
        ...tier,
        isPrize: true,
        reward: buildDefaultTierPrize(accountToDisplay, subscriptionPlans)
      };
    } else {
      newTiers[tierIndex] = {
        ...tier,
        isPrize: false,
        reward: getDefaultTierTokenReward(tier.id)
      };
    }
    setFieldValue("lty.tiers", newTiers as LoyaltyTier[]);
  };

  const handleGenerateIdeas = async (tierIndex: number) => {
    const suggestion = (await NiceModal.show(GiftSuggestionModal)) as
      | { name?: string; image?: string }
      | undefined;
    const tier = tiers[tierIndex];
    if (typeof tier.reward !== "object") return;
    const prize = tier.reward as LoyaltyTierPrize;
    if (suggestion?.name || suggestion?.image) {
      setFieldValue(`lty.tiers[${tierIndex}].reward`, {
        ...prize,
        ...(suggestion?.name && { name: suggestion.name }),
        ...(suggestion?.image && { image: suggestion.image })
      });
    }
  };

  return (
    <LoyaltyRewardEditorPageLayout
      id="loyalty-gift-cards-container"
      titleKey="loyalty_prizes_section_title"
      className="main-gift-config"
      contentClassName="w-100 d-flex flex-column st-gap px-4 align-items-center"
    >
      <div className="w-100 p-0 m-0 st-gap gift-cards-container">
        {tiers.map((tier, tierIndex) => (
          <TierEditorCard
            key={tier.id}
            tier={tier}
            tierIndex={tierIndex}
            currentLang={currentLang}
            setFieldValue={setFieldValue}
            hasAdvancedCouponOptions={hasAdvancedCouponOptions}
            onCustomize={() => handleTierRewardType(tierIndex, true)}
            onStandard={() => handleTierRewardType(tierIndex, false)}
            onGenerateIdeas={() => handleGenerateIdeas(tierIndex)}
          />
        ))}
      </div>
      <KC_Padder />
    </LoyaltyRewardEditorPageLayout>
  );
};

export default LoyaltyTiersGiftsEditor;
